import type { TranslationOptions } from '@topolo-io/ui-kit';

type OfflineTranslator = (key: string, options?: TranslationOptions) => string;
let offlineTranslator: { owner: symbol; t: OfflineTranslator } | null = null;

/** Registration follows the committed root provider; stale cleanup cannot clear a newer owner. */
export function registerOfflineTranslator(t: OfflineTranslator): () => void {
  const owner = Symbol('roadmapper-offline-translator');
  offlineTranslator = { owner, t };
  return () => { if (offlineTranslator?.owner === owner) offlineTranslator = null; };
}

function queuedAcknowledgement(): string {
  if (!offlineTranslator) throw new Error('Offline acknowledgement requires a native translation provider');
  const { t } = offlineTranslator;
  return t('roadmapper.offline.request-queued');
}

/**
 * Offline Handler for PWA
 * 
 * Provides comprehensive offline support including:
 * - API response caching
 * - Offline queue for failed requests
 * - Sync when connection is restored
 * - Conflict resolution for concurrent edits
 */

interface CachedResponse {
  url: string;
  method: string;
  body?: unknown;
  response: unknown;
  timestamp: number;
  etag?: string;
}

interface QueuedRequest {
  id: string;
  url: string;
  method: string;
  body?: unknown;
  headers?: Record<string, string>;
  timestamp: number;
  retryCount: number;
}

interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncTime: number;
  pendingRequests: number;
  failedRequests: number;
}

export class OfflineHandler {
  private cache: Map<string, CachedResponse> = new Map();
  private requestQueue: Map<string, QueuedRequest> = new Map();
  private syncCallbacks: Set<(status: SyncStatus) => void> = new Set();
  private isSyncing = false;
  private lastSyncTime = 0;
  private dbName = 'roadmapper-offline';
  private dbVersion = 2;
  private db: IDBDatabase | null = null;
  private readonly ready: Promise<void>;

  constructor() {
    this.ready = this.initializeOfflineStorage();
    this.setupEventListeners();
  }

  private async initializeOfflineStorage(): Promise<void> {
    await this.initIndexedDB();
    await Promise.all([
      this.loadCacheFromIndexedDB(),
      this.loadQueueFromIndexedDB(),
      this.loadStatusFromIndexedDB(),
    ]);
    this.notifyStatusChange();
  }

  whenReady(): Promise<void> {
    return this.ready;
  }

  /**
   * Initialize IndexedDB for persistent offline storage
   */
  private async initIndexedDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Cache store for API responses
        if (!db.objectStoreNames.contains('cache')) {
          const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
          cacheStore.createIndex('timestamp', 'timestamp');
        }

        // Queue store for pending requests
        if (!db.objectStoreNames.contains('queue')) {
          const queueStore = db.createObjectStore('queue', { keyPath: 'id' });
          queueStore.createIndex('timestamp', 'timestamp');
        }

        if (!db.objectStoreNames.contains('status')) {
          db.createObjectStore('status', { keyPath: 'key' });
        }
      };
    });
  }

  /**
   * Setup online/offline event listeners
   */
  private setupEventListeners(): void {
    window.addEventListener('online', () => {
      this.handleOnlineEvent();
    });

    window.addEventListener('offline', () => {
      this.handleOfflineEvent();
    });

    // Listen for visibility change to sync when app becomes active
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && navigator.onLine) {
        this.syncPendingRequests();
      }
    });
  }

  /**
   * Handle when the app comes back online
   */
  private async handleOnlineEvent(): Promise<void> {
    this.notifyStatusChange();
    await this.syncPendingRequests();
  }

  /**
   * Handle when the app goes offline
   */
  private handleOfflineEvent(): void {
    this.notifyStatusChange();
  }

  /**
   * Cache API response with intelligent expiration
   */
  async cacheResponse(
    url: string,
    method: string,
    body: unknown,
    response: unknown,
    etag?: string,
    scope = '',
  ): Promise<void> {
    await this.ready;
    const key = this.generateCacheKey(url, method, body, scope);
    const cachedResponse: CachedResponse = {
      url,
      method,
      body,
      response,
      timestamp: Date.now(),
      etag,
    };

    this.cache.set(key, cachedResponse);
    await this.saveCacheToIndexedDB(key, cachedResponse);
  }

  /**
   * Get cached response if available and valid
   */
  async getCachedResponse(
    url: string,
    method: string,
    body?: unknown,
    scope = '',
  ): Promise<unknown | null> {
    await this.ready;
    const key = this.generateCacheKey(url, method, body, scope);
    const cached = this.cache.get(key);

    if (!cached) {
      return null;
    }

    // Check if cache is still valid (24 hours for most data, 1 hour for real-time data)
    const maxAge = this.getCacheMaxAge(url);
    const isExpired = Date.now() - cached.timestamp > maxAge;

    if (isExpired) {
      this.cache.delete(key);
      await this.removeCacheFromIndexedDB(key);
      return null;
    }

    return cached.response;
  }

  /**
   * Queue request for later execution when online
   */
  async queueRequest(
    url: string,
    method: string,
    body?: unknown,
    headers?: Record<string, string>
  ): Promise<string> {
    await this.ready;
    const id = this.generateRequestId();
    const queuedRequest: QueuedRequest = {
      id,
      url,
      method,
      body,
      headers,
      timestamp: Date.now(),
      retryCount: 0,
    };

    this.requestQueue.set(id, queuedRequest);
    await this.saveQueueToIndexedDB(queuedRequest);
    this.notifyStatusChange();

    return id;
  }

  /**
   * Sync all pending requests when online
   */
  async syncPendingRequests(): Promise<void> {
    await this.ready;
    const requests = Array.from(this.requestQueue.values())
      .filter(request => request.retryCount < 3)
      .sort((a, b) => a.timestamp - b.timestamp);

    if (!navigator.onLine || requests.length === 0) {
      return;
    }

    this.isSyncing = true;
    this.notifyStatusChange(); // Set syncing state

    let completedRequest = false;
    try {
      for (const request of requests) {
        try {
          await this.executeQueuedRequest(request);
          this.requestQueue.delete(request.id);
          await this.removeQueueFromIndexedDB(request.id);
          this.lastSyncTime = Date.now();
          completedRequest = true;
        } catch (error) {
          console.error(`Failed to sync request ${request.id}:`, error);
          request.retryCount++;
          await this.saveQueueToIndexedDB(request);
        }
      }
      if (completedRequest) {
        await this.saveStatusToIndexedDB();
      }
    } finally {
      this.isSyncing = false;
      this.notifyStatusChange();
    }
  }

  /**
   * Execute a queued request
   */
  private async executeQueuedRequest(request: QueuedRequest): Promise<void> {
    const response = await fetch(request.url, {
      method: request.method,
      headers: {
        'Content-Type': 'application/json',
        ...request.headers,
      },
      body: request.body ? JSON.stringify(request.body) : undefined,
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status} ${response.statusText}`);
    }

    // Cache the successful response
    const responseData = await response.json();
    await this.cacheResponse(
      request.url,
      request.method,
      request.body,
      responseData,
      response.headers.get('etag') || undefined,
      this.getRequestScope(request.headers),
    );
  }

  /**
   * Enhanced fetch with offline support
   */
  async offlineFetch(
    url: string,
    options: RequestInit = {},
    // Transport used for the live network hop. Callers pass the auth-client's
    // authenticated fetch here so online requests carry the bearer/refresh
    // contract; the offline cache/queue behavior wraps around it.
    transport: typeof fetch = fetch
  ): Promise<Response> {
    const method = options.method || 'GET';
    const body = options.body ? JSON.parse(options.body as string) : undefined;
    const scope = this.getRequestScope(options.headers);

    // If online, make the request and cache the response
    if (navigator.onLine) {
      try {
        const response = await transport(url, options);
        
        if (response.ok && (method === 'GET' || method === 'HEAD')) {
          const cacheableResponse = response.clone();
          void cacheableResponse.json().then(responseData =>
            this.cacheResponse(
              url,
              method,
              body,
              responseData,
              response.headers.get('etag') || undefined,
              scope,
            ),
          ).catch(() => undefined);
        }

        return response;
      } catch (error) {
        // Network error, fall back to cache or queue
        if (method === 'GET' || method === 'HEAD') {
          const cached = await this.getCachedResponse(url, method, body, scope);
          if (cached) {
            return new Response(JSON.stringify(cached), {
              status: 200,
              headers: { 'Content-Type': 'application/json' },
            });
          }
        }
        throw error;
      }
    }

    // If offline, reads use persisted data and writes queue for later.
    if (method === 'GET' || method === 'HEAD') {
      const cached = await this.getCachedResponse(url, method, body, scope);
      if (cached) {
        return new Response(JSON.stringify(cached), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      throw new Error('No cached data available for offline access');
    } else {
      // Resolve acknowledgement before persisting the write, so refusal has no queue effect.
      const message = queuedAcknowledgement();
      // Queue write operations for later sync
      await this.queueRequest(url, method, body, options.headers as Record<string, string>);
      
      // Return optimistic response for write operations
      return new Response(JSON.stringify({ 
        success: true, 
        queued: true,
        message
      }), {
        status: 202, // Accepted
        headers: { 'Content-Type': 'application/json' },
      });
    }
  }

  /**
   * Subscribe to sync status changes
   */
  onStatusChange(callback: (status: SyncStatus) => void): () => void {
    this.syncCallbacks.add(callback);
    
    // Call immediately with current status
    callback(this.getStatus());
    
    // Return unsubscribe function
    return () => {
      this.syncCallbacks.delete(callback);
    };
  }

  /**
   * Get current sync status
   */
  getStatus(): SyncStatus {
    const requests = Array.from(this.requestQueue.values());
    const failedRequests = requests.filter(request => request.retryCount >= 3).length;
    return {
      isOnline: navigator.onLine,
      isSyncing: this.isSyncing,
      lastSyncTime: this.lastSyncTime,
      pendingRequests: requests.length - failedRequests,
      failedRequests,
    };
  }

  /**
   * Clear all cached data (useful for logout)
   */
  async clearCache(): Promise<void> {
    await this.ready;
    this.cache.clear();
    this.requestQueue.clear();
    this.lastSyncTime = 0;
    
    if (this.db) {
      const transaction = this.db.transaction(['cache', 'queue', 'status'], 'readwrite');
      await Promise.all([
        this.waitForRequest(transaction.objectStore('cache').clear()),
        this.waitForRequest(transaction.objectStore('queue').clear()),
        this.waitForRequest(transaction.objectStore('status').clear()),
      ]);
    }
    this.notifyStatusChange();
  }

  // Private helper methods

  private generateCacheKey(url: string, method: string, body?: unknown, scope = ''): string {
    const bodyString = body ? JSON.stringify(body) : '';
    return `${scope}:${method}:${url}:${bodyString}`;
  }

  private getRequestScope(headers?: HeadersInit): string {
    const requestHeaders = new Headers(headers);
    const scopeParts = [
      requestHeaders.get('X-App-ID') || '',
      requestHeaders.get('X-Topolo-Resource-Type') || '',
      requestHeaders.get('X-Topolo-Resource-ID') || '',
    ];
    return scopeParts.some(Boolean) ? scopeParts.join(':') : '';
  }

  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private getCacheMaxAge(url: string): number {
    // Real-time data (dashboard metrics) - 5 minutes
    if (url.includes('/dashboard') || url.includes('/metrics')) {
      return 5 * 60 * 1000;
    }
    
    // Project/roadmap lists - 1 hour
    if (url.includes('/projects') || url.includes('/roadmaps')) {
      return 60 * 60 * 1000;
    }
    
    // Individual resources - 24 hours
    return 24 * 60 * 60 * 1000;
  }

  private notifyStatusChange(): void {
    const status = this.getStatus();
    this.syncCallbacks.forEach(callback => callback(status));
  }

  private async saveCacheToIndexedDB(key: string, data: CachedResponse): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction(['cache'], 'readwrite');
    const store = transaction.objectStore('cache');
    await this.waitForRequest(store.put({ key, ...data }));
  }

  private async removeCacheFromIndexedDB(key: string): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction(['cache'], 'readwrite');
    const store = transaction.objectStore('cache');
    await this.waitForRequest(store.delete(key));
  }

  private async saveQueueToIndexedDB(request: QueuedRequest): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction(['queue'], 'readwrite');
    const store = transaction.objectStore('queue');
    await this.waitForRequest(store.put(request));
  }

  private async removeQueueFromIndexedDB(id: string): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction(['queue'], 'readwrite');
    const store = transaction.objectStore('queue');
    await this.waitForRequest(store.delete(id));
  }

  private async loadCacheFromIndexedDB(): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction(['cache'], 'readonly');
    const store = transaction.objectStore('cache');
    const cachedItems = await this.waitForRequest(
      store.getAll() as IDBRequest<Array<{ key: string } & CachedResponse>>
    );
    cachedItems.forEach(item => {
      const { key, ...cachedResponse } = item;
      this.cache.set(key, cachedResponse);
    });
  }

  private async loadQueueFromIndexedDB(): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction(['queue'], 'readonly');
    const store = transaction.objectStore('queue');
    const queuedItems = await this.waitForRequest(
      store.getAll() as IDBRequest<QueuedRequest[]>
    );
    queuedItems.forEach(item => {
      this.requestQueue.set(item.id, item);
    });
  }

  private async loadStatusFromIndexedDB(): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction(['status'], 'readonly');
    const status = await this.waitForRequest(
      transaction.objectStore('status').get('sync') as IDBRequest<
        { key: string; lastSyncTime: number } | undefined
      >
    );
    this.lastSyncTime = status?.lastSyncTime ?? 0;
  }

  private async saveStatusToIndexedDB(): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction(['status'], 'readwrite');
    await this.waitForRequest(
      transaction.objectStore('status').put({
        key: 'sync',
        lastSyncTime: this.lastSyncTime,
      })
    );
  }

  private waitForRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }
}

// Lazily-constructed singleton. Constructing eagerly at module scope opened an
// IndexedDB connection as a side effect of merely IMPORTING this module (or
// anything that reaches it, such as the api client), which fired during module
// evaluation before the app had decided it needed offline storage, and made the
// module unimportable in any environment without IndexedDB. Construction now
// happens on first real use.
let handlerInstance: OfflineHandler | null = null;

export function getOfflineHandler(): OfflineHandler {
  if (!handlerInstance) {
    handlerInstance = new OfflineHandler();
  }
  return handlerInstance;
}

/** Test seam: drop the instance so the next call builds a fresh one. */
export function resetOfflineHandlerForTests(): void {
  handlerInstance = null;
}

// Enhanced fetch function that can be used throughout the app
export const offlineFetch = (url: string, options?: RequestInit, transport?: typeof fetch) =>
  getOfflineHandler().offlineFetch(url, options, transport);

// Export types for use in components
export type { SyncStatus };
